import { Coffee, Sparkles, ArrowRight, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const STRIPE_LINK = 'https://buy.stripe.com/9B600kgubgxxesl5VE3VC0r';

export default function BonusBatch() {
  return (
    <div className="min-h-screen bg-[#132947]">
      <Header />

      {/* Hero */}
      <div className="relative min-h-screen overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1511923963667-3054f3ce9431?w=1920&q=80)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#132947]/85 via-[#132947]/92 to-[#132947]" />
        </div>

        <div className="absolute top-20 right-10 w-72 h-72 bg-amber-600/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-orange-900/10 rounded-full blur-3xl" />

        <div className="relative min-h-screen flex items-center justify-center px-6 max-w-7xl mx-auto py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center w-full">
            <div className="text-center lg:text-left space-y-6">
              <div className="relative">
                <div className="absolute -left-4 top-8 w-1 h-32 bg-gradient-to-b from-amber-500 to-transparent rounded-full hidden lg:block" />
                <h1 className="text-6xl md:text-8xl font-bold text-white mb-4 tracking-tight leading-none">
                  Bonus
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500 mt-2">
                    Batch
                  </span>
                </h1>
              </div>

              <div className="space-y-4 pt-2">
                <div className="flex items-center gap-3 justify-center lg:justify-start">
                  <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-500/50" />
                  <span className="text-amber-400/80 text-sm font-semibold uppercase tracking-wider">
                    Lucky Little Extras
                  </span>
                  <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-500/50" />
                </div>

                <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
                  Different coffee. Different amount. No guarantees.
                </p>

                <p className="text-lg md:text-xl text-white/70 font-light">
                  Just a small batch of very good coffee that got lucky.
                </p>

                <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-2">
                  <span className="text-3xl font-bold text-amber-400">$8</span>
                  <span className="text-white/60 text-sm">per 4oz bag</span>
                  <span className="text-amber-400/60 text-sm">•</span>
                  <span className="text-amber-400/80 text-sm font-medium">Marblehead only</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
                <a
                  href={STRIPE_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative px-12 py-6 rounded-xl font-bold text-xl transition-all duration-300 inline-flex items-center justify-center gap-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 shadow-2xl shadow-amber-700/40 transform hover:scale-105 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                  <Coffee className="w-6 h-6 group-hover:rotate-12 transition-transform" />
                  Grab a Batch
                </a>
                <a
                  href="#details-section"
                  className="bg-white/5 text-white border-2 border-white/20 px-10 py-6 rounded-xl font-semibold text-lg hover:bg-white/10 hover:border-amber-500/50 transition-all duration-300 backdrop-blur-md inline-flex items-center justify-center"
                >
                  Learn More
                </a>
              </div>
            </div>

            <div className="flex items-center justify-center lg:justify-end relative">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-transparent rounded-3xl blur-3xl scale-110" />
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-amber-500/30 to-transparent rounded-3xl blur-xl" />
                <div className="relative w-full max-w-md aspect-[3/4] rounded-2xl shadow-2xl border-2 border-white/10 overflow-hidden bg-gradient-to-br from-amber-950/40 to-[#132947] flex items-center justify-center p-8">
                  <div className="text-center space-y-6">
                    <Sparkles className="w-20 h-20 text-amber-400 mx-auto" />
                    <div className="space-y-2">
                      <p className="text-amber-400 text-sm font-bold uppercase tracking-widest">Bonus</p>
                      <h2 className="text-5xl font-bold text-white tracking-tight">Batch</h2>
                      <p className="text-white/50 text-sm">It got lucky.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details */}
      <div id="details-section" className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            What's in the bag?
          </h2>
          <div className="w-20 h-1 bg-amber-500 mx-auto rounded-full" />
        </div>

        <div className="bg-gradient-to-br from-amber-600/10 to-transparent border border-amber-600/20 rounded-2xl p-8 md:p-12">
          <div className="text-white/80 text-lg leading-relaxed max-w-3xl mx-auto space-y-6">
            <p>
              Every week, we end up with a little extra coffee from roasting.
            </p>
            <p>
              Still fresh. Still great. Just not enough to become a full batch.
            </p>
            <p>
              So we bag it up, call it <span className="font-semibold text-amber-400">Bonus Batch</span>, and let it go until it's gone.
            </p>
            <p>
              Different coffee. Different amount. No guarantees.
            </p>
            <p className="font-medium text-white/90">
              Just a small batch of very good coffee that got lucky. It will be varied at times.
            </p>
          </div>
        </div>

        {/* Info cards */}
        <div className="grid md:grid-cols-3 gap-4 mt-12">
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
            <Coffee className="w-8 h-8 text-amber-400 mx-auto mb-3" />
            <h4 className="font-semibold text-white mb-1">Always Fresh</h4>
            <p className="text-white/60 text-sm">Roasted weekly, bagged on the spot</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
            <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-3" />
            <h4 className="font-semibold text-white mb-1">Always Different</h4>
            <p className="text-white/60 text-sm">Could be any origin, any roast profile</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
            <ArrowRight className="w-8 h-8 text-amber-400 mx-auto mb-3" />
            <h4 className="font-semibold text-white mb-1">Until It's Gone</h4>
            <p className="text-white/60 text-sm">When it's out, it's out. Next week brings a new one.</p>
          </div>
        </div>
      </div>

      {/* Pricing & CTA */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        <div className="bg-gradient-to-br from-amber-600/15 to-transparent border-2 border-amber-600/40 rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Grab One Before It's Gone
          </h2>
          <p className="text-white/70 text-xl mb-8">
            Whatever's in this week's bag, it's good coffee that just happened to be extra.
          </p>

          <div className="flex items-center justify-center gap-3 mb-8">
            <MapPin className="w-5 h-5 text-amber-400" />
            <span className="text-white/80 text-lg">Marblehead delivery only</span>
          </div>

          <div className="inline-flex items-baseline gap-2 mb-8">
            <span className="text-5xl md:text-6xl font-bold text-white">$8</span>
            <span className="text-white/70 text-lg">per 4oz bag</span>
          </div>

          <div>
            <a
              href={STRIPE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-10 py-5 rounded-lg font-bold text-xl transition-all duration-300 bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 shadow-xl shadow-amber-700/30 hover:scale-105 transform"
            >
              <Coffee className="w-6 h-6" />
              Grab a Batch
            </a>
            <p className="text-white/60 text-sm mt-4">
              Free local delivery in Marblehead
            </p>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="border-t border-white/10 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Want something more predictable?
          </h3>
          <p className="text-white/70 text-xl mb-8">
            Check out our full lineup of named roasts — same fresh quality, every time.
          </p>
          <Link
            to="/"
            className="bg-white/10 text-white border border-white/20 px-8 py-5 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 inline-flex items-center justify-center"
          >
            Explore Bond Coffee
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
